import {
    isAbstractSketchEdgeObject,
    type CollectSource,
    type ISketchObject,
    SketchCollectionObject,
    type SketchPointObject,
} from '../../mesh/sketch';
import {addObject3DToParent} from '../../utils/scene-object/addObject3DToParent';
import {SketchClosureResolver} from '../closure';
import {SketchRectangleConstructionLineResolver} from '../rectangle';

/**
 * 草图闭合集合副本构建器。
 *
 * 职责说明：
 * 1. 根据一次复制上下文中的边副本重建闭合集合；
 * 2. 把闭合边共享端点统一收口到副本集合，保持与真实绘制结果一致的对象树；
 * 3. 委托矩形构造线解析器重映射集合内的辅助对象运行时引用；
 * 4. 不负责创建边副本、复制约束或执行几何变换。
 */
export class SketchCopiedClosedCollectionBuilder {
    /** 矩形构造线归属解析服务。 */
    private readonly rectangleConstructionLineResolver: SketchRectangleConstructionLineResolver;

    /**
     * 创建草图闭合集合副本构建器。
     *
     * @param rectangleConstructionLineResolver 矩形构造线归属解析服务。
     */
    public constructor(
        rectangleConstructionLineResolver = new SketchRectangleConstructionLineResolver(),
    ) {
        this.rectangleConstructionLineResolver = rectangleConstructionLineResolver;
    }

    /**
     * 根据源集合与复制映射构建闭合集合副本。
     *
     * @param sourceCollection 源闭合集合。
     * @param objectMap 源草图对象 UUID 到副本对象的映射。
     * @param rebuiltSourceCloneIds 已由新集合接管的副本边 ID 集合。
     * @returns 拓扑完整的副本集合；边副本不完整时返回 null。
     */
    public build(
        sourceCollection: SketchCollectionObject,
        objectMap: ReadonlyMap<string, ISketchObject>,
        rebuiltSourceCloneIds: Set<string>,
    ): SketchCollectionObject | null {
        const clonedSources = Array.from(sourceCollection.collectSet)
            .map((source) => objectMap.get(source.uuid))
            .filter((clone): clone is CollectSource => !!clone && isAbstractSketchEdgeObject(clone));
        if (clonedSources.length !== sourceCollection.collectSet.size) {
            return null;
        }

        const copiedCollection = new SketchCollectionObject({
            referencePlane: sourceCollection.referencePlane,
            sources: clonedSources,
            creationSource: sourceCollection.creationSource,
        });
        copiedCollection.name = sourceCollection.name;

        // 闭合边构造完成后再统一提升共享端点，避免复制服务把角点遗留在草图组顶层。
        this.attachSourceControlPoints(copiedCollection, clonedSources);

        const copiedGeometryData = this.rectangleConstructionLineResolver
            .cloneGeometryDataForCopiedCollection(sourceCollection, objectMap);
        if (copiedGeometryData) {
            copiedCollection.geometryData = copiedGeometryData;
        }
        copiedCollection.syncContour();

        clonedSources.forEach((source) => {
            rebuiltSourceCloneIds.add(source.uuid);
        });
        return copiedCollection;
    }

    /**
     * 将闭合边的唯一端点统一挂载到副本集合。
     *
     * 设计说明：
     * 1. 单独复制边时，端点会先作为草图组直接子对象进入场景；
     * 2. 闭合轮廓完成重建后，这些端点应恢复为集合直接子对象；
     * 3. 统一按 UUID 去重，避免相邻边共享端点被重复重挂。
     *
     * @param copiedCollection 副本闭合集合。
     * @param clonedSources 副本闭合边。
     */
    private attachSourceControlPoints(
        copiedCollection: SketchCollectionObject,
        clonedSources: readonly CollectSource[],
    ): void {
        const pointMap = new Map<string, SketchPointObject>();
        clonedSources.forEach((source) => {
            const endpoints = SketchClosureResolver.getSourceEndpoints(source);
            pointMap.set(endpoints.startPoint.uuid, endpoints.startPoint);
            pointMap.set(endpoints.endPoint.uuid, endpoints.endPoint);
        });

        pointMap.forEach((point) => {
            if (point.parent !== copiedCollection) {
                addObject3DToParent(point, copiedCollection);
            }
        });
    }
}
